import type { LayoutFrameMode } from "@tc-superpods/app-contract";
import { type JSX, type ReactNode } from "react";
import { TooltipProvider } from "./ui/tooltip";

export interface AppShellProps {
  /** `shell` paints the content panel. `app` leaves the space unpainted for the mounted app to fill. */
  frame: LayoutFrameMode;
  sidebar: ReactNode;
  mobileHeader: ReactNode;
  children: ReactNode;
}

/**
 * The shell layout: persistent collapsible sidebar plus a content panel
 * floating on the canvas. The panel keeps its top/left inset at 16px below
 * `md` and 32px at `md` and up, while the right/bottom inset is half those
 * values. The breakpoint follows `--tc-breakpoint-md` (see useIsMobile).
 */
export function AppShell({ frame, sidebar, mobileHeader, children }: AppShellProps): JSX.Element {
  return (
    <TooltipProvider delayDuration={150}>
      {/*
        box-border is explicit on both boxes: Tailwind preflight is never imported,
        so these default to content-box and `h-full` + `p-2` would overflow the
        shell root by 16px — clipping the panel's bottom border and radius.
      */}
      <div className="box-border flex h-full w-full flex-col overflow-hidden shell-canvas p-2 pt-0 font-body md:pt-2">
        {mobileHeader}
        <div className="flex min-h-0 min-w-0 flex-1">
          {sidebar}
          <div
            data-platform-frame={frame}
            className={
              frame === "app"
                ? "box-border ml-0 flex min-w-0 flex-1 md:ml-2"
                : "border-travel-spot box-border ml-0 flex min-w-0 flex-1 overflow-hidden rounded-panel border border-border bg-surface p-4 pb-2 pr-2 shadow-panel-inset md:ml-2 md:p-8 md:pb-4 md:pr-4"
            }
          >
            {children}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
